import { estimateReason } from "../estimate-reasons";
import type { EstimatedSummary, MoneySummary } from "@/types/spend";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { estimateDisplay, formatGbp, moneyAmountDisplay } from "../money-format";
import { SpendQualityLabel } from "./spend-quality-label";

export const SpendHeadline = ({
  estimated,
  estimatedTotal,
  actual,
  actualQualityFlags,
}: {
  estimated: EstimatedSummary;
  estimatedTotal: EstimatedSummary;
  actual: MoneySummary;
  actualQualityFlags: string[];
}) => {
  const { formatDate } = useDateFormatter();
  return (
    <section className="grid gap-4 md:grid-cols-3" aria-label="Spend summary">
      <div className="rounded-lg border border-[#ddd5cb] bg-white p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Estimated (Annual)</div>
        <div className="mt-2 text-2xl font-bold text-[#111b35]">{estimateDisplay(estimated)}</div>
        {estimated.reasons?.map(reason => (
          <p key={reason} className="mt-1 text-xs text-amber-800">
            {estimateReason(reason)}
          </p>
        ))}
        <p className="mt-2 text-xs text-gray-500">Total contract value: {estimateDisplay(estimatedTotal)}</p>
        <p className="mt-1 text-xs text-gray-500">
          Valued for this period{estimated.valuation_date ? ` on ${formatDate(estimated.valuation_date)}` : ""}
          {estimated.fx_source ? ` · ${estimated.fx_source}` : ""}.
        </p>
      </div>
      <div className="rounded-lg border border-[#ddd5cb] bg-white p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Actual</div>
        <div className="mt-2 text-2xl font-bold text-[#111b35]">{moneyAmountDisplay(actual)}</div>
        <div className="mt-2">
          <SpendQualityLabel money={actual} qualityFlags={actualQualityFlags} />
        </div>
      </div>
      <div className="rounded-lg border border-[#ddd5cb] bg-white p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Credits</div>
        <div className="mt-2 text-2xl font-bold text-[#111b35]">{formatGbp(actual.credits_gbp) ?? "—"}</div>
        <p className="mt-2 text-xs text-gray-500">
          {actual.credit_count} credit line{actual.credit_count === 1 ? "" : "s"}; shown separately from positive spend.
        </p>
      </div>
    </section>
  );
};
